import * as React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { SchoolRounded, Dashboard as DashboardIcon } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { RequestPage } from "@mui/icons-material";
import { Collapse, Typography, alpha, useTheme } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";

export default function TemporaryDrawer() {
  const navigate = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const [openRequest, setOpenRequest] = React.useState(false);
  const [openTutor, setOpenTutor] = React.useState(false);

  const currentPath = navigate.pathname;

  const newData = [
    {
      title: t('navbar.tutors'),
      icon: <SchoolRounded />,
      subItems: [
        { title: t('request.all'), icon: <HowToRegIcon />, link: "/tutor/all" },
        { title: t('request.pending'), icon: <HourglassEmptyIcon />, link: "/tutor/pending" },
        { title: t('request.approved'), icon: <CheckCircleIcon />, link: "/tutor/approved" },
        { title: t('request.rejected'), icon: <CancelIcon />, link: "/tutor/rejected" },
      ],
    },
    {
      title: t('navbar.requests'),
      icon: <RequestPage />,
      subItems: [
        { title: t('request.pending'), icon: <HourglassEmptyIcon />, link: "/request/pending" },
        { title: t('request.approved'), icon: <CheckCircleIcon />, link: "/request/approved" },
        { title: t('request.rejected'), icon: <CancelIcon />, link: "/request/rejected" },
      ],
    },
  ];

  React.useEffect(() => {
    if (currentPath.includes('/request')) {
      setOpenRequest(true);
    }
    if (currentPath.includes('/tutor')) {
      setOpenTutor(true);
    }
  }, [currentPath]);

  const handleRequestClick = () => {
    setOpenRequest(!openRequest);
  };

  const handleTutorClick = () => {
    setOpenTutor(!openTutor);
  };

  const isActive = (path) => {
    return currentPath === path;
  };

  const isSubItemActive = (link) => {
    return currentPath === link;
  };

  const list = (
    <Box 
      sx={{ 
        width: '100%',
        height: '100%',
        bgcolor: 'white',
        borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }} 
      role="presentation"
    >
      <Box
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          flexShrink: 0,
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            color: theme.palette.primary.main,
            textAlign: 'center',
            fontSize: { xs: '1.1rem', md: '1.5rem' },
          }}
        >
          {t('navbar.dashboard')}
        </Typography>
      </Box>

      <List 
        sx={{ 
          pt: 2,
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 2,
        }}
      >
        <ListItem 
          component={Link}
          to="/dashboard"
          disablePadding
          sx={{
            mb: 1,
            mx: 1,
            borderRadius: 2,
            bgcolor: isActive('/dashboard') ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }
          }}
        >
          <ListItemButton sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ color: isActive('/dashboard') ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 40, md: 56 } }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('navbar.dashboard')}
              primaryTypographyProps={{
                fontWeight: isActive('/dashboard') ? 700 : 500,
                color: isActive('/dashboard') ? theme.palette.primary.main : 'text.primary',
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, mx: 2 }} />

        <ListItem 
          disablePadding
          sx={{
            mb: 1,
            mx: 1,
            borderRadius: 2,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }
          }}
        >
          <ListItemButton onClick={handleRequestClick} sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ color: openRequest ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 40, md: 56 } }}>
              <RequestPage />
            </ListItemIcon>
            <ListItemText 
              primary={t('navbar.requests')}
              primaryTypographyProps={{
                fontWeight: openRequest ? 700 : 500,
                color: openRequest ? theme.palette.primary.main : 'text.primary',
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            />
            {openRequest ? <ExpandLess sx={{ color: theme.palette.primary.main }} /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openRequest} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {newData
              .find((item) => item.title === t('navbar.requests'))
              .subItems.map((subItem, index) => (
                <ListItem
                  key={index}
                  disablePadding
                  component={Link}
                  to={subItem.link}
                  sx={{
                    mb: 0.5,
                    mx: 1,
                    borderRadius: 2,
                    bgcolor: isSubItemActive(subItem.link) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }
                  }}
                >
                  <ListItemButton sx={{ pl: 4, py: 1 }}>
                    <ListItemIcon sx={{ color: isSubItemActive(subItem.link) ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 36, md: 40 } }}>
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={subItem.title}
                      primaryTypographyProps={{
                        fontSize: { xs: '0.85rem', md: '0.9rem' },
                        fontWeight: isSubItemActive(subItem.link) ? 700 : 500,
                        color: isSubItemActive(subItem.link) ? theme.palette.primary.main : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Collapse>

        <ListItem 
          disablePadding
          sx={{
            mb: 1,
            mx: 1,
            borderRadius: 2,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }
          }}
        >
          <ListItemButton onClick={handleTutorClick} sx={{ py: 1.5 }}>
            <ListItemIcon sx={{ color: openTutor ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 40, md: 56 } }}>
              <SchoolRounded />
            </ListItemIcon>
            <ListItemText 
              primary={t('navbar.tutors')}
              primaryTypographyProps={{
                fontWeight: openTutor ? 700 : 500,
                color: openTutor ? theme.palette.primary.main : 'text.primary',
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            />
            {openTutor ? <ExpandLess sx={{ color: theme.palette.primary.main }} /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openTutor} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {newData
              .find((item) => item.title === t('navbar.tutors'))
              .subItems.map((subItem, index) => (
                <ListItem
                  key={index}
                  disablePadding
                  component={Link}
                  to={subItem.link}
                  sx={{
                    mb: 0.5,
                    mx: 1,
                    borderRadius: 2,
                    bgcolor: isSubItemActive(subItem.link) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }
                  }}
                >
                  <ListItemButton sx={{ pl: 4, py: 1 }}>
                    <ListItemIcon sx={{ color: isSubItemActive(subItem.link) ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 36, md: 40 } }}>
                      {subItem.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={subItem.title}
                      primaryTypographyProps={{
                        fontSize: { xs: '0.85rem', md: '0.9rem' },
                        fontWeight: isSubItemActive(subItem.link) ? 700 : 500,
                        color: isSubItemActive(subItem.link) ? theme.palette.primary.main : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: { xs: '100%', md: 280 },
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: '100%', md: 280 },
          boxSizing: "border-box",
          position: { xs: 'relative', md: 'relative' },
          height: "100%",
          overflow: "hidden",
          border: 'none',
          borderRight: { xs: 'none', md: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` },
        },
      }}
    >
      {list}
    </Drawer>
  );
}
